/* localization v2 */
import { Component } from "@theme/component";
import { normalizeString } from "@theme/utilities";

/**
 * A custom element that displays a localization form.
 *
 * @typedef {object} FormRefs
 * @property {HTMLDivElement} countryList - The country list element.
 * @property {HTMLInputElement} countryInput - The country input element.
 * @property {HTMLUListElement[]} countryListItems - The country list items element.
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLDivElement} liveRegion - The live region element.
 * @property {HTMLSelectElement} languageInput - The language input element.
 * @property {HTMLSpanElement} noResultsMessage - The no results message element.
 * @property {HTMLUListElement} popularCountries - The popular countries element.
 * @property {HTMLInputElement} search - The search input element.
 * @property {HTMLButtonElement} resetButton - The reset button element.
 *
 * @extends {Component<FormRefs>}
 */
class LocalizationFormComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.refs.search && this.refs.search.addEventListener("keydown", this.#onSearchKeyDown);
    this.refs.countryList &&
      this.refs.countryList.addEventListener("keydown", this.#onContainerKeyDown);
    this.refs.countryList &&
      this.refs.countryList.addEventListener("scroll", this.#onCountryListScroll, {
        passive: true,
      });

    // 국가 변경 시 언어 옵션 필터링
    this.refs.countryInput?.addEventListener("change", this.#onCountryChange);
    this.#filterLanguagesByCountry();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.search && this.refs.search.removeEventListener("keydown", this.#onSearchKeyDown);
    this.refs.countryList &&
      this.refs.countryList.removeEventListener("keydown", this.#onContainerKeyDown);
    this.refs.countryList &&
      this.refs.countryList.removeEventListener("scroll", this.#onCountryListScroll, {
        passive: true,
      });
    this.refs.countryInput?.removeEventListener("change", this.#onCountryChange);
  }

  /**
   * Handles the keydown event for the container.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onContainerKeyDown = (event) => {
    const { countryInput, countryListItems, form } = this.refs;

    switch (event.key) {
      case "ArrowUp":
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus("UP");
        break;
      case "ArrowDown":
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus("DOWN");
        break;
      case "Enter":
        event.preventDefault();
        event.stopPropagation();
        const focusedItem = countryListItems.find(
          (item) => item.getAttribute("aria-selected") === "true"
        );

        if (focusedItem) {
          countryInput.value = focusedItem.dataset.value ?? "";
          form.submit();
        }
        break;
    }

    if (!this.refs.search) return;

    setTimeout(() => {
      const focusableItems = this.refs.countryListItems.filter(
        (item) => !item.hasAttribute("hidden")
      );
      const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
      const focusedItem = focusableItems[focusedItemIndex];

      if (focusedItem) {
        this.refs.search.setAttribute("aria-activedescendant", focusedItem.id);
      } else {
        this.refs.search.setAttribute("aria-activedescendant", "");
      }
    });
  };

  /**
   * 국가 변경 시 언어 옵션 필터링
   */
  #onCountryChange = () => {
    this.#filterLanguagesByCountry();
  };

  /**
   * data-countries 속성을 기반으로 현재 선택된 국가에서
   * 사용 가능한 언어 옵션만 표시합니다.
   * data-countries가 없는 옵션은 모든 국가에서 표시됩니다.
   */
  #filterLanguagesByCountry() {
    const { countryInput, languageInput } = this.refs;
    if (!countryInput || !languageInput) return;

    const selectedCountry = countryInput.value;

    for (const option of languageInput.options) {
      const restrictedTo = option.dataset.countries;

      if (!restrictedTo) {
        // data-countries 없음 → 항상 표시 (영어 등)
        option.hidden = false;
      } else {
        const allowed = restrictedTo.split(",").map((c) => c.trim());
        option.hidden = !allowed.includes(selectedCountry);
      }
    }

    // 현재 선택된 언어가 숨겨진 경우 첫 번째 보이는 옵션으로 변경
    const selected = languageInput.options[languageInput.selectedIndex];
    if (selected?.hidden) {
      for (const option of languageInput.options) {
        if (!option.hidden) {
          languageInput.value = option.value;
          break;
        }
      }
    }
  }

  /**
   * Selects a country.
   *
   * @param {string} countryName - The name of the country to select.
   * @param {Event} event - The event object.
   */
  selectCountry = (countryName, event) => {
    event.preventDefault();
    const { countryInput, form } = this.refs;

    countryInput.value = countryName;
    form?.submit();
  };

  /**
   * Changes the language of the localization form.
   *
   * @param {Event} event - The event object.
   */
  changeLanguage(event) {
    const { form, languageInput } = this.refs;
    const value = event.target instanceof HTMLSelectElement ? event.target.value : null;

    if (value) {
      languageInput.value = value;
      form.submit();
    }
  }

  /**
   * Finds matches for a given search value in a country element.
   *
   * @typedef {Object} Options
   * @property {boolean} [matchLabel] - Whether to match the label.
   * @property {boolean} [matchAlias] - Whether to match the alias.
   * @property {boolean} [matchIso] - Whether to match the iso.
   * @property {boolean} [matchCurrency] - Whether to match the currency.
   * @property {boolean} [labelMatchStart] - Whether to match the label start.
   * @property {boolean} [aliasExactMatch] - Whether to match the alias exact match.
   *
   * @typedef {Object} MatchTypes
   * @property {boolean} [label] - Whether the label matches the search value.
   * @property {boolean} [alias] - Whether the alias matches the search value.
   * @property {boolean} [iso] - Whether the iso matches the search value.
   * @property {boolean} [currency] - Whether the currency matches the search value.
   *
   * @param {string} searchValue - The search value to find matches for.
   * @param {HTMLElement} countryEl - The country element to find matches in.
   * @param {Options} options - The options for the search.
   * @returns {MatchTypes} The matches found in the country element.
   */
  #findMatches(
    searchValue,
    countryEl,
    options = {
      // Which data types (label, alias, iso) to match against
      matchLabel: true,
      matchAlias: true,
      matchIso: true,
      matchCurrency: true,
      // If true, the search value must match the start of the label
      labelMatchStart: false,
      // If true, a result will not display unless the search value equals an alias in its entirety
      aliasExactMatch: false,
    }
  ) {
    let matchTypes = {};
    const { aliases, value: iso } = countryEl.dataset;

    if (options.matchLabel) {
      const countryName = normalizeString(countryEl.querySelector(".country")?.textContent ?? "");

      if (!countryName) return matchTypes;

      matchTypes.label = options.labelMatchStart
        ? countryName.startsWith(searchValue)
        : countryName.includes(searchValue);
    }

    if (options.matchCurrency) {
      const currency = normalizeString(
        countryEl.querySelector(".localization-form__currency")?.textContent ?? ""
      );
      matchTypes.currency = currency.includes(searchValue);
    }

    if (options.matchIso) {
      matchTypes.iso = normalizeString(iso ?? "") == searchValue;
    }

    if (options.matchAlias) {
      const countryAliases = aliases?.split(",").map((alias) => normalizeString(alias));

      if (!countryAliases) return matchTypes;

      matchTypes.alias =
        countryAliases.length > 0 &&
        countryAliases.find((alias) =>
          options.aliasExactMatch ? alias === searchValue : alias.startsWith(searchValue)
        ) !== undefined;
    }

    return matchTypes;
  }

  /**
   * Highlights matching text in a string by wrapping it in <mark> tags.
   *
   * @param {string | null} text - The text to highlight.
   * @param {string} searchValue - The search value to highlight.
   * @returns {string} The text with matching parts wrapped in <mark> tags.
   */
  #highlightMatches(text, searchValue) {
    if (!text || !searchValue) return text ?? "";

    const normalizedText = normalizeString(text);
    const normalizedSearch = normalizeString(searchValue);
    const startIndex = normalizedText.indexOf(normalizedSearch);

    if (startIndex === -1) return text;

    const endIndex = startIndex + normalizedSearch.length;
    const before = text.slice(0, startIndex);
    const match = text.slice(startIndex, endIndex);
    const after = text.slice(endIndex);

    let result = "";
    if (before) {
      result += `<mark>${before}</mark>`;
    }
    result += match;
    if (after) {
      result += `<mark>${after}</mark>`;
    }
    return result;
  }

  /**
   * Filters the countries based on the search value.
   */
  filterCountries() {
    const {
      countryList,
      countryListItems,
      liveRegion,
      noResultsMessage,
      popularCountries,
      resetButton,
      search,
    } = this.refs;
    const { labelResultsCount } = this.dataset;
    const searchValue = normalizeString(search.value);
    let countVisibleCountries = 0;

    resetButton.toggleAttribute("hidden", !searchValue);

    if (popularCountries) {
      popularCountries.toggleAttribute("hidden", Boolean(searchValue));
    }

    const wrapper = this.querySelector(".country-selector-form__wrapper");
    if (wrapper) {
      wrapper.classList.toggle("is-searching", !!searchValue);
    }

    for (const countryEl of countryListItems) {
      if (searchValue === "") {
        countryEl.removeAttribute("hidden");
        const countrySpan = countryEl.querySelector(".country");
        if (countrySpan) {
          countrySpan.textContent = countrySpan.textContent;
        }
        countVisibleCountries++;
      } else {
        const matches = this.#findMatches(searchValue, countryEl);

        // In the future, we could reorder/rank filtered results based on the match types
        if (matches.label || matches.alias || matches.iso || matches.currency) {
          countryEl.removeAttribute("hidden");
          const countrySpan = countryEl.querySelector(".country");
          if (countrySpan) {
            countrySpan.innerHTML = this.#highlightMatches(countrySpan.textContent, searchValue);
          }
          countVisibleCountries++;
        } else {
          countryEl.setAttribute("hidden", "");
        }
      }
    }

    if (liveRegion && labelResultsCount) {
      liveRegion.innerText = labelResultsCount.replace("[count]", `${countVisibleCountries}`);
    }

    noResultsMessage.hidden = countVisibleCountries > 0;
    countryList.scrollTop = 0;
  }

  /**
   * Changes the focus of the country list items.
   *
   * @param {string} direction - The direction to change the focus.
   */
  #changeCountryFocus(direction) {
    const { countryListItems } = this.refs;
    const focusableItems = countryListItems.filter((item) => !item.hasAttribute("hidden"));
    const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
    const focusedItem = focusableItems[focusedItemIndex];
    let itemToFocus;

    if (direction === "UP") {
      itemToFocus =
        focusedItemIndex > 0
          ? focusableItems[focusedItemIndex - 1]
          : focusableItems[focusableItems.length - 1];
    } else {
      itemToFocus =
        focusedItemIndex < focusableItems.length - 1
          ? focusableItems[focusedItemIndex + 1]
          : focusableItems[0];
    }

    if (focusedItem) {
      focusedItem.setAttribute("aria-selected", "false");
    }
    itemToFocus?.setAttribute("aria-selected", "true");
    itemToFocus?.focus();
  }

  /**
   * Resets the countries filter.
   *
   * @param {Event} event - The event object.
   */
  resetCountriesFilter(event) {
    const { search } = this.refs;

    event.stopPropagation();
    search.value = "";
    this.filterCountries();
    search.setAttribute("aria-activedescendant", "");
    search.focus();
  }

  /**
   * Handles the keydown event for the search input.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.#onContainerKeyDown(event);
  };

  /**
   * Resets the form.
   */
  resetForm() {
    const { search } = this.refs;

    if (!search) return;

    if (search.value != "") {
      search.value = "";
      this.filterCountries();
      search.setAttribute("aria-activedescendant", "");
    }
  }

  /**
   * Focuses the search input.
   */
  focusSearchInput = () => {
    const { search } = this.refs;

    search?.focus();
  };

  /**
   * Handles the scroll event on the country list.
   *
   * @param {Event} event - The scroll event object.
   */
  #onCountryListScroll = (event) => {
    const countryFilter = this.querySelector(".country-filter");
    const countryList = event.target instanceof HTMLElement ? event.target : null;

    if (countryFilter && countryList) {
      const shouldShowBorder = countryList.scrollTop > 0;
      countryFilter.classList.toggle("is-scrolled", shouldShowBorder);
    }
  };
}

/**
 * A custom element that displays a modal localization form.
 * The modal panel is moved to document.body on open to ensure
 * position:fixed works correctly regardless of parent CSS.
 *
 * @typedef {object} DropdownRefs
 * @property {HTMLButtonElement} toggle - The toggle button element.
 *
 * @extends {Component<DropdownRefs>}
 */
class DropdownLocalizationComponent extends Component {
  /** @type {HTMLDivElement|null} */
  #panel = null;

  connectedCallback() {
    super.connectedCallback();

    // <template ref="template"> 내용을 DOM에 삽입 (template은 inert → 수동 확장 필요)
    const tpl = this.refs.template || this.querySelector("template");
    if (tpl && tpl.content) {
      this.appendChild(document.importNode(tpl.content, true));
      tpl.remove();
    }

    // template 확장 후 패널 참조
    this.#panel = this.querySelector(".localization-modal-overlay") || this.querySelector(".localization-wrapper");

    // .localization-wrapper → 모달 구조로 변환 (Liquid 스니펫 미반영 대응)
    if (this.#panel && !this.#panel.classList.contains("localization-modal-overlay")) {
      this.#panel.classList.remove("localization-wrapper", "absolute", "color-background", "opacity-0");
      this.#panel.classList.add("localization-modal-overlay");
      // 내부 콘텐츠를 모달 카드로 감싸기
      const inner = this.#panel.innerHTML;
      this.#panel.innerHTML = `
        <div class="localization-modal-card color-background">
          <div class="localization-modal__header">
            <h2 class="localization-modal__title text-body-sm">Region &amp; Language</h2>
            <button type="button" class="localization-modal__close button-unstyled cursor-pointer" aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12.5 3.5L3.5 12.5M3.5 3.5l9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            </button>
          </div>
          ${inner}
        </div>`;
    }

    // 닫기 버튼 바인딩
    const closeBtn = this.#panel?.querySelector(".localization-modal__close");
    if (closeBtn) {
      closeBtn.addEventListener("click", this.hidePanel);
    }
  }

  disconnectedCallback() {
    document.removeEventListener("keyup", this.#handleKeyUp);
    // 패널이 body로 이동된 경우 원래 위치로 복귀
    if (this.#panel && this.#panel.parentElement === document.body) {
      this.appendChild(this.#panel);
    }
    super.disconnectedCallback();
  }

  get isHidden() {
    return !this.#panel || this.#panel.hasAttribute("hidden");
  }

  /**
   * 모달 토글
   */
  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  /**
   * 모달 열기, position:fixed가 부모 CSS에 영향 받지 않도록
   * 패널을 body로 이동
   */
  showPanel() {
    if (!this.#panel) return;
    if (!this.isHidden) return;

    // body로 이동하여 position:fixed 정상 동작 보장
    if (this.#panel.parentElement !== document.body) {
      document.body.appendChild(this.#panel);
    }

    document.addEventListener("keyup", this.#handleKeyUp);
    this.#panel.addEventListener("click", this.#handleOverlayClick);

    this.#panel.removeAttribute("hidden");
    this.refs.toggle?.setAttribute("aria-expanded", "true");

    // 스크롤 잠금
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
  }

  /**
   * 모달 닫기 및 스크롤 복원
   */
  hidePanel = () => {
    if (!this.#panel || this.isHidden) return;

    document.removeEventListener("keyup", this.#handleKeyUp);
    this.#panel.removeEventListener("click", this.#handleOverlayClick);
    this.refs.toggle?.setAttribute("aria-expanded", "false");
    this.#panel.setAttribute("hidden", "");

    // 스크롤 복원
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
  };

  /**
   * 오버레이(카드 바깥) 클릭 시 닫기
   *
   * @param {PointerEvent} event
   */
  #handleOverlayClick = (event) => {
    const card = this.#panel?.querySelector(".localization-modal-card") || this.#panel?.querySelector("localization-form-component");
    if (card && !card.contains(event.target)) {
      this.hidePanel();
    } else if (!card && event.target === this.#panel) {
      // 카드 래퍼 없는 구조: 패널 배경 직접 클릭 시 닫기
      this.hidePanel();
    }
  };

  /**
   * ESC 키로 닫기
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyUp = (event) => {
    if (event.key === "Escape") {
      this.hidePanel();
      event.stopPropagation();
      this.refs.toggle?.focus();
    }
  };
}

if (!customElements.get("localization-form-component")) {
  customElements.define("localization-form-component", LocalizationFormComponent);
}

if (!customElements.get("dropdown-localization-component")) {
  customElements.define("dropdown-localization-component", DropdownLocalizationComponent);
}
